import type { Localized } from '../types/notice'
import type { DashboardStats } from '../data/requestDashboard'
import { FLAGGED_REQUESTS, POTHOLE_ON_TIME } from '../data/requestDashboard'
import { fmtDec, fmtNum, pctChange, scopeHasFlagged } from './requestDashboard'

export interface AtnaDataRow {
  label: Localized
  value: number
  /** Unit shown after the value, e.g. " %" or " j". */
  unit: string
  tone: 'ok' | 'risk' | 'late' | 'neutral'
}

export interface AtnaDataAnswer {
  text: Localized
  rows?: AtnaDataRow[]
  /** Max value used to scale the bars (defaults to 100). */
  max?: number
  source: Localized
}

export interface AtnaDataScope {
  /** e.g. "District 1", or null for the whole city. */
  label: Localized | null
  districtId: string | null
  period: Localized
  stats: DashboardStats
}

export const ATNA_DATA_SUGGESTIONS: Localized[] = [
  { fr: 'Quels dossiers nécessitent mon attention ?', en: 'Which files need my attention?' },
  { fr: 'Quels problèmes reviennent le plus ?', en: 'Which issues come up most?' },
  { fr: 'Où les délais se dégradent-ils ?', en: 'Where are turnaround times slipping?' },
  { fr: 'Quels résultats présenter aux citoyens ?', en: 'Which results should I share with citizens?' },
]

const has = (q: string, words: string[]) => words.some((w) => q.includes(w))

/** Canned natural-language answers, built from the dashboard's demo figures. */
export function answerDataQuestion(question: string, scope: AtnaDataScope): AtnaDataAnswer {
  const q = question.toLowerCase()
  const s = scope.stats
  const where: Localized = scope.label
    ? { fr: ` dans le ${scope.label.fr}`, en: ` in ${scope.label.en}` }
    : { fr: ' à l’échelle de la ville', en: ' city-wide' }
  const source: Localized = {
    fr: `Requêtes · ${scope.label ? scope.label.fr : 'toute la ville'} · ${scope.period.fr.toLowerCase()}`,
    en: `Requests · ${scope.label ? scope.label.en : 'whole city'} · ${scope.period.en.toLowerCase()}`,
  }
  const flagged = scopeHasFlagged(scope.districtId)
  const { late, atRisk, sector } = FLAGGED_REQUESTS

  // Where are turnaround times slipping?
  if (has(q, ['dégrad', 'degrad', 'slipping', 'worse', 'where', 'où'])) {
    const geo: Localized = flagged
      ? {
          fr: ` Sur le territoire, la seule demande en retard et les ${atRisk.count} proches de l’échéance sont toutes en ${sector.fr}.`,
          en: ` Across the territory, the only late request and the ${atRisk.count} close to their deadline are all in ${sector.en}.`,
        }
      : {
          fr: ` Aucune demande de vos secteurs n’est en retard ni proche de l’échéance ; le tableau par service de la carte donne le résultat de chaque secteur.`,
          en: ` No request in your sectors is late or close to its deadline; the map’s service table shows each sector’s result.`,
        }
    return {
      text: {
        fr: `Un seul service se dégrade à l’échelle de la ville : Nid-de-poule, à ${POTHOLE_ON_TIME.current} % dans les délais contre ${POTHOLE_ON_TIME.previous} % la période précédente, sous le seuil de conformité de ${POTHOLE_ON_TIME.threshold} %. Les autres services restent au-dessus.${geo.fr}`,
        en: `Only one service is slipping city-wide: Pothole, at ${POTHOLE_ON_TIME.current}% on time versus ${POTHOLE_ON_TIME.previous}% the previous period, below the ${POTHOLE_ON_TIME.threshold}% compliance threshold. The other services stay above.${geo.en}`,
      },
      rows: [
        { label: { fr: 'Nid-de-poule', en: 'Pothole' }, value: POTHOLE_ON_TIME.current, unit: ' %', tone: 'risk' },
        { label: { fr: 'Éclairage de parc', en: 'Park lighting' }, value: 89, unit: ' %', tone: 'ok' },
        { label: { fr: 'Réparation lampadaire', en: 'Streetlight repair' }, value: 92, unit: ' %', tone: 'ok' },
        { label: { fr: 'Signalisation routière', en: 'Road signage' }, value: 94, unit: ' %', tone: 'ok' },
        { label: { fr: 'Bac brisé', en: 'Broken bin' }, value: 96, unit: ' %', tone: 'ok' },
      ],
      source: { fr: 'Respect des SLA · toute la ville · période en cours', en: 'SLA compliance · whole city · current period' },
    }
  }

  // Which results should I share with citizens?
  if (has(q, ['résultat', 'resultat', 'présenter', 'presenter', 'valoris', 'result', 'share', 'satisf'])) {
    return {
      text: {
        fr: `${s.onTime} % des demandes ont été résolues dans les délais${where.fr} (${s.onTimePrev} % la période précédente), avec un délai médian de ${fmtDec(s.medianDays, 'fr')} jours (${fmtDec(s.medianDaysPrev, 'fr')} auparavant). Les citoyens donnent ${fmtDec(s.satisfaction, 'fr')} / 5 après traitement, sur ${fmtNum(s.surveyResponses, 'fr')} réponses au sondage (${s.surveyRate} % des demandes fermées).${s.late === 0 ? ' Aucune demande n’est en retard à ce jour.' : ''}`,
        en: `${s.onTime}% of requests were resolved on time${where.en} (${s.onTimePrev}% the previous period), with a median time of ${fmtDec(s.medianDays, 'en')} days (${fmtDec(s.medianDaysPrev, 'en')} before). Citizens rate ${fmtDec(s.satisfaction, 'en')} / 5 after handling, from ${fmtNum(s.surveyResponses, 'en')} survey responses (${s.surveyRate}% of closed requests).${s.late === 0 ? ' No request is late as of today.' : ''}`,
      },
      rows: [
        { label: { fr: 'Résolues dans les délais', en: 'Resolved on time' }, value: s.onTime, unit: ' %', tone: 'ok' },
        { label: { fr: 'Satisfaction (sur 100)', en: 'Satisfaction (out of 100)' }, value: Math.round(s.satisfaction * 20), unit: '', tone: 'ok' },
      ],
      source,
    }
  }

  // Which issues come up most?
  if (has(q, ['revienn', 'problème', 'probleme', 'fréquent', 'catégor', 'combien', 'volume', 'issue', 'most', 'how many'])) {
    const [first, second] = s.issues
    return {
      text: {
        fr: `${fmtNum(s.received, 'fr')} demandes reçues${where.fr} (${pctChange(s.received, s.receivedPrev, 'fr')} par rapport à la période précédente). L’${first.label.fr.toLowerCase()} arrive en tête avec ${fmtNum(first.count, 'fr')} demandes (${pctChange(first.count, first.previous, 'fr')}), devant les ${second.label.fr.toLowerCase()} (${fmtNum(second.count, 'fr')}).`,
        en: `${fmtNum(s.received, 'en')} requests received${where.en} (${pctChange(s.received, s.receivedPrev, 'en')} from the previous period). ${first.label.en} leads with ${fmtNum(first.count, 'en')} requests (${pctChange(first.count, first.previous, 'en')}), ahead of ${second.label.en.toLowerCase()} (${fmtNum(second.count, 'en')}).`,
      },
      rows: s.issues.map((i) => ({ label: i.label, value: i.count, unit: '', tone: 'neutral' as const })),
      max: first.count,
      source,
    }
  }

  // Which files need my attention?
  if (has(q, ['attention', 'dossier', 'nécessit', 'urgent', 'retard', 'late', 'file', 'need'])) {
    if (!flagged || s.late + s.openAtRisk === 0) {
      return {
        text: {
          fr: `Aucun dossier en retard ni proche de l’échéance${where.fr}. Les ${s.open} demandes en cours sont toutes dans les délais. Point à suivre : le service Nid-de-poule est à ${POTHOLE_ON_TIME.current} % à l’échelle de la ville, sous le seuil de ${POTHOLE_ON_TIME.threshold} %.`,
          en: `No file is late or close to its deadline${where.en}. All ${s.open} open requests are on time. Worth following: the Pothole service is at ${POTHOLE_ON_TIME.current}% city-wide, below the ${POTHOLE_ON_TIME.threshold}% threshold.`,
        },
        rows: [
          { label: { fr: 'En cours', en: 'Open' }, value: s.open, unit: '', tone: 'ok' },
          { label: { fr: 'Proches de l’échéance', en: 'Close to deadline' }, value: 0, unit: '', tone: 'risk' },
          { label: { fr: 'En retard', en: 'Late' }, value: 0, unit: '', tone: 'late' },
        ],
        max: Math.max(1, s.open),
        source,
      }
    }
    return {
      text: {
        fr: `${s.late + s.openAtRisk} dossiers sur ${s.open} en cours demandent votre attention${where.fr}, tous en ${sector.fr} : 1 ${late.service.fr.toLowerCase()} en retard (${late.days} jours pour un SLA de ${late.sla} ; motif au dossier : pluies abondantes) et ${atRisk.count} demandes de ${atRisk.service.fr.toLowerCase()} à ${atRisk.days} jours sur ${atRisk.sla}.`,
        en: `${s.late + s.openAtRisk} of ${s.open} open files need your attention${where.en}, all in ${sector.en}: 1 late ${late.service.en.toLowerCase()} (${late.days} days against a ${late.sla}-day SLA; reason in the file: heavy rain) and ${atRisk.count} ${atRisk.service.en.toLowerCase()} requests at ${atRisk.days} of ${atRisk.sla} days.`,
      },
      rows: [
        { label: { fr: `${late.service.fr} · secteur B`, en: `${late.service.en} · sector B` }, value: late.days, unit: ` / ${late.sla} j`, tone: 'late' },
        { label: { fr: 'Bac brisé · secteur B', en: 'Broken bin · sector B' }, value: atRisk.days, unit: ` / ${atRisk.sla} j`, tone: 'risk' },
        { label: { fr: 'Bac brisé · secteur B', en: 'Broken bin · sector B' }, value: atRisk.days, unit: ` / ${atRisk.sla} j`, tone: 'risk' },
      ],
      max: 14,
      source: { fr: `Demandes ouvertes au 30 sept. 2026`, en: `Open requests as of Sept. 30, 2026` },
    }
  }

  return {
    text: {
      fr: 'Je n’ai pas trouvé de donnée correspondante. Je peux vous répondre sur les dossiers à suivre, les problèmes les plus fréquents, l’évolution des délais ou les résultats à présenter aux citoyens.',
      en: 'I couldn’t find matching data. I can answer about files to follow, the most frequent issues, turnaround trends or results to share with citizens.',
    },
    source,
  }
}
